import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import usePrevious from 'lib/src/hooks/usePrevious';
import useForm from '@components/form/hooks/useForm';

import { updateSuperAdminUser } from '@actions/superAdminUsers';
import {
    getSuperAdminUserIsPosting,
    getSuperAdminUserPostSuccess,
    getSuperAdminUsersPostError,
} from '@selectors/superAdminUsers';
import { useHistory } from 'react-router-dom';

import { SuperAdminUser } from 'src/types/shared/SuperAdminUser';

export default function useUpdateSuperAdmin({
    id,
    email,
    firstName,
    lastName,
}: SuperAdminUser): UpdateSuperAdminReturn {
    const dispatch = useDispatch();
    const history = useHistory();

    const closeModal = useCallback(() => {
        history.replace(`/superadmins/${id}`);
    }, [history, id]);

    const [formState, handleChange] = useForm({ email, firstName, lastName });

    const handleSubmit = useCallback(() => {
        dispatch(updateSuperAdminUser(id, formState));
    }, [dispatch, id, formState]);

    const isPosting = useSelector(getSuperAdminUserIsPosting);
    const error = useSelector(getSuperAdminUsersPostError);
    const postSuccess = useSelector(getSuperAdminUserPostSuccess);
    const prevPostSuccess = usePrevious(postSuccess);

    useEffect(() => {
        if (!prevPostSuccess && postSuccess) {
            closeModal();
        }
    }, [postSuccess, prevPostSuccess, closeModal]);

    return {
        handleSubmit,
        formState,
        handleChange,
        closeModal,
        isPosting,
        error,
    };
}

interface FormState {
    email: string;
    firstName: string;
    lastName: string;
}
interface UpdateSuperAdminReturn {
    handleSubmit: () => void;
    formState: FormState;
    handleChange: <T>(name: keyof FormState, value: T) => void;
    closeModal: () => void;
    isPosting: boolean;
    error: string | null;
}
